"use client";

import {
  Badge,
  Button,
  Checkbox,
  Label,
  Loader,
  Modal,
  toast,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, PackageIcon, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import {
  canUseMerchantPermission,
  shopPosApi,
} from "@/modules/merchant/presentation/api/client";
import {
  useAddShopProductClaimAttachment,
  useCancelShopProductClaim,
  useRemoveShopProductClaimAttachment,
  useSetShopProductClaimStatus,
  useShopProductClaimDetailQuery,
  useSubmitShopProductClaim,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";
import {
  claimIssueTypeLabel,
  claimLineDisplayFromSale,
  claimStatusBadge,
  warrantyEligibilityLabel,
} from "./shop-product-claim-helpers";

export function ShopProductClaimDetailPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const claimId = params?.claimId as string | undefined;
  const nav = useNavigate();
  const { user } = useAuthState();
  const { currentShop, currentPermissions } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;

  const detailQuery = useShopProductClaimDetailQuery(shopId, claimId);
  const detail = detailQuery.data;

  const saleForClaimDetailQuery = useQuery({
    queryKey: ["shop-product-claim-detail-sale", shopId, detail?.posSaleId],
    queryFn: async () => {
      if (!shopId || !detail?.posSaleId) throw new Error("missing");
      return shopPosApi.getSale(shopId, detail.posSaleId);
    },
    enabled: !!shopId && !!detail?.posSaleId,
  });

  const submitClaim = useSubmitShopProductClaim(shopId);
  const cancelClaim = useCancelShopProductClaim(shopId);
  const setStatus = useSetShopProductClaimStatus(shopId);
  const addAttachment = useAddShopProductClaimAttachment(shopId);
  const removeAttachment = useRemoveShopProductClaimAttachment(shopId);

  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.claims.create,
    verifiedLevel,
  );
  const canManage = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.claims.manage,
    verifiedLevel,
  );

  const isCreator =
    !!detail && !!user?.id && detail.createdByUserId === user.id;
  const canEditDraft =
    !!detail &&
    canCreate &&
    detail.status === "draft" &&
    (canManage || isCreator);

  const [manageDialog, setManageDialog] = useState<
    null | "resolved" | "rejected"
  >(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [applyStockImpact, setApplyStockImpact] = useState(false);

  useEffect(() => {
    if (!claimId || detail?.id !== claimId) return;
    setManageDialog(null);
    setResolutionNote("");
    setApplyStockImpact(false);
  }, [claimId, detail?.id]);

  const goBackToList = () => {
    if (!shopId) return;
    void nav({
      to: "/app/shops/$shopId/product-claims",
      params: { shopId },
      search: {
        offset: 0,
        limit: 20,
        branchId: detail?.branchId,
        status: undefined,
        supplierId: undefined,
        warrantyLink: undefined,
      },
    });
  };

  if (!shopId || !claimId) return null;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.claims.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.productClaimsDetail(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <h1 className="font-heading font-semibold text-xl tracking-tight">
            ລາຍລະອຽດເຄສ
          </h1>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2 h-8 gap-1"
            onClick={() => goBackToList()}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບລາຍການ
          </Button>
        </div>

        <div className="rounded-xl border bg-card p-4 sm:p-6">
          {detailQuery.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : detailQuery.isError || !detail ? (
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm">
                ບໍ່ພົບເຄສ ຫຼື ບໍ່ມີສິດເຂົ້າເບິ່ງ
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => goBackToList()}
              >
                ກັບລາຍການ
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {claimStatusBadge(detail.status)}
                <span className="text-muted-foreground text-sm">
                  {detail.branchName} · {claimIssueTypeLabel(detail.issueType)}
                </span>
                {detail.saleLineWarrantyId ? (
                  <Badge
                    variant="outline"
                    className="border-sky-500/40 text-sky-800 text-xs dark:text-sky-200"
                  >
                    ຜູກສິດປະກັນ
                  </Badge>
                ) : null}
                {detail.warrantyEligibilityAtSubmit ? (
                  <Badge variant="secondary" className="text-xs">
                    {warrantyEligibilityLabel(
                      detail.warrantyEligibilityAtSubmit,
                    )}
                  </Badge>
                ) : null}
              </div>
              {canEditDraft ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    className="bg-orange-600 hover:bg-orange-600"
                    onClick={() => {
                      void nav({
                        to: "/app/shops/$shopId/product-claims/$claimId/edit",
                        params: { shopId, claimId: detail.id },
                      });
                    }}
                  >
                    ແກ້ໄຂຮ່າງ
                  </Button>
                </div>
              ) : null}
              {detail.title ? (
                <p className="font-medium">{detail.title}</p>
              ) : null}
              {detail.description ? (
                <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                  {detail.description}
                </p>
              ) : null}
              {detail.supplierName ? (
                <p className="text-muted-foreground text-sm">
                  ຜູ້ສະໜອງ:{" "}
                  <span className="text-foreground">{detail.supplierName}</span>
                </p>
              ) : null}
              {detail.stockImpactAppliedAt ? (
                <p className="text-muted-foreground text-sm">
                  ຕັດສະຕ໋ອກຊຳຮຸດຕາມເຄສ:{" "}
                  <span className="text-foreground">
                    {formatDateLocal(detail.stockImpactAppliedAt)}
                  </span>
                </p>
              ) : null}
              {detail.posSaleId ? (
                <div className="text-muted-foreground text-sm">
                  <span>ບິນຂາຍ POS: </span>
                  {saleForClaimDetailQuery.isLoading ? (
                    <span className="text-muted-foreground">…</span>
                  ) : saleForClaimDetailQuery.data ? (
                    <span className="text-foreground">
                      {saleForClaimDetailQuery.data.saleNumber} ·{" "}
                      {formatDateLocal(saleForClaimDetailQuery.data.createdAt)}
                    </span>
                  ) : (
                    <span className="font-mono text-foreground">
                      {detail.posSaleId}
                    </span>
                  )}
                </div>
              ) : null}
              {detail.odometerAtClaimKm != null ? (
                <p className="text-muted-foreground text-sm">
                  ເລກໄມລ໌ຕອນເປີດເຄສ:{" "}
                  <span className="font-mono text-foreground">
                    {detail.odometerAtClaimKm.toLocaleString()} km
                  </span>
                </p>
              ) : null}
              {detail.vehicleNote?.trim() ? (
                <div className="text-muted-foreground text-sm">
                  <span className="block text-xs">ລູກຄ້າ / ຍານພາຫະນະ</span>
                  <p className="mt-0.5 whitespace-pre-wrap text-foreground">
                    {detail.vehicleNote.trim()}
                  </p>
                </div>
              ) : null}
              {detail.posSaleId && saleForClaimDetailQuery.isLoading ? (
                <p className="text-muted-foreground text-xs">
                  ກໍາລັງໂຫຼດລາຍລະອຽດບິນ…
                </p>
              ) : null}
              <ul className="space-y-3">
                {detail.lines.map((line) => {
                  const row = claimLineDisplayFromSale(
                    line,
                    saleForClaimDetailQuery.data,
                  );
                  return (
                    <li
                      key={line.id}
                      className="overflow-hidden rounded-xl border border-border/70 bg-muted/15"
                    >
                      <div className="flex gap-3 p-3">
                        <div className="relative size-16 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted/80 sm:size-20">
                          <AppImage
                            src={row.imageUrl}
                            alt={row.productName?.trim() || "ສິນຄ້າ"}
                            className="size-full"
                            fit="cover"
                            fallback={
                              <div
                                className="flex size-full items-center justify-center bg-muted"
                                aria-hidden
                              >
                                <PackageIcon className="size-8 text-muted-foreground/70" />
                              </div>
                            }
                          />
                        </div>
                        <div className="min-w-0 flex-1 space-y-2">
                          <p className="font-heading font-medium text-foreground text-sm leading-snug">
                            {row.productName?.trim() || "—"}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-muted-foreground">
                              ລະຫັດຮ້ານ
                            </span>
                            <code className="rounded-md border border-border/80 bg-background px-2 py-0.5 font-mono text-foreground">
                              {row.shopSku}
                            </code>
                            <span className="text-muted-foreground">
                              × {line.qty}
                            </span>
                          </div>
                          {line.inventoryLotId || line.inventorySerialId ? (
                            <div className="flex flex-wrap gap-2 border-border/50 border-t pt-2">
                              {line.inventoryLotId ? (
                                <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs">
                                  <span className="shrink-0 font-medium text-muted-foreground">
                                    Lot no.
                                  </span>
                                  <span className="min-w-0 truncate font-mono text-foreground">
                                    {row.lotNo?.trim() || "—"}
                                  </span>
                                </span>
                              ) : null}
                              {line.inventorySerialId ? (
                                <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs">
                                  <span className="shrink-0 font-medium text-muted-foreground">
                                    Serial no.
                                  </span>
                                  <span className="min-w-0 truncate font-mono text-foreground">
                                    {row.serialNo?.trim() || "—"}
                                  </span>
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <p className="border-border/50 border-t pt-2 text-muted-foreground text-xs">
                              ບໍ່ມີ lot / serial ກັບບັນທັດນີ້
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="space-y-2">
                <Label>ໜ່ວຍຫຼັກຖານ (ຮ່າງເທົ່ານັ້ນ)</Label>
                {canCreate &&
                detail.status === "draft" &&
                (canManage || isCreator) ? (
                  <PresignedFileUpload
                    keyPrefix="uploads/shop-product-claims"
                    accept="image/*,application/pdf"
                    buttonLabel="ອັບໂຫຼດໄຟລ໌"
                    hint="ຮູບ ຫຼື PDF — ຫຼັງອັບໂຫຼດຈະບັນທຶກເຂົ້າເຄສອັດຕະໂນມັດ"
                    onSuccess={(storageKey) =>
                      addAttachment.mutateAsync({
                        claimId: detail.id,
                        body: {
                          storageKey,
                          contentType: null,
                          fileName: null,
                        },
                      })
                    }
                  />
                ) : (
                  <p className="text-muted-foreground text-xs">
                    ແກ້ໄຂໜ່ວຍຫຼັກຖານໄດ້ສະເພາະເຄສຮ່າງ ທີ່ທ່ານມີສິດແກ້ໄຂ
                  </p>
                )}
                <ul className="divide-y rounded-md border text-sm">
                  {(detail.attachments ?? []).map((a) => {
                    const href = resolveImageSrc(a.storageKey);
                    return (
                      <li
                        key={a.id}
                        className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <a
                            href={href ?? "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sky-600 hover:underline"
                          >
                            {a.fileName?.trim()
                              ? a.fileName
                              : (a.storageKey.split("/").pop() ?? a.storageKey)}
                          </a>
                          {a.contentType ? (
                            <span className="ml-2 text-muted-foreground text-xs">
                              ({a.contentType})
                            </span>
                          ) : null}
                          <p className="text-muted-foreground text-xs">
                            {formatDateLocal(a.createdAt)}
                          </p>
                        </div>
                        {canCreate &&
                        detail.status === "draft" &&
                        (canManage || isCreator) ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="shrink-0 text-destructive hover:text-destructive"
                            disabled={removeAttachment.isPending}
                            onClick={() =>
                              removeAttachment.mutateAsync({
                                claimId: detail.id,
                                attachmentId: a.id,
                              })
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
                {(detail.attachments ?? []).length === 0 ? (
                  <p className="text-muted-foreground text-xs">ຍັງບໍ່ມີໄຟລ໌</p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {canCreate &&
                detail.status === "draft" &&
                (canManage || isCreator) ? (
                  <Button
                    type="button"
                    className="bg-orange-600 hover:bg-orange-600"
                    disabled={submitClaim.isPending}
                    onClick={async () => {
                      await submitClaim.mutateAsync(detail.id);
                    }}
                  >
                    {submitClaim.isPending ? <Loader /> : "ສົ່ງເຄສ"}
                  </Button>
                ) : null}
                {(canManage || (canCreate && isCreator)) &&
                ["draft", "submitted", "under_review"].includes(
                  detail.status,
                ) ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={cancelClaim.isPending}
                    onClick={async () => {
                      await cancelClaim.mutateAsync(detail.id);
                    }}
                  >
                    {cancelClaim.isPending ? <Loader /> : "ຍົກເລີກເຄສ"}
                  </Button>
                ) : null}
              </div>

              {canManage &&
              ["submitted", "under_review"].includes(detail.status) ? (
                <div className="space-y-3 border-t pt-4">
                  <Label>ຈັດການສະຖານະ (ຜູ້ຈັດການ)</Label>
                  <div className="flex flex-wrap gap-2">
                    {detail.status === "submitted" ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={setStatus.isPending}
                        onClick={async () => {
                          await setStatus.mutateAsync({
                            claimId: detail.id,
                            body: {
                              status: "under_review",
                              applyStockImpact: false,
                              resolutionNote: undefined,
                            },
                          });
                        }}
                      >
                        {setStatus.isPending ? <Loader /> : "ກຳລັງກວດ"}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      className="bg-orange-600 hover:bg-orange-600"
                      disabled={setStatus.isPending}
                      onClick={() => {
                        setResolutionNote("");
                        setApplyStockImpact(false);
                        setManageDialog("resolved");
                      }}
                    >
                      ປິດເຄສ
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={setStatus.isPending}
                      onClick={() => {
                        setResolutionNote("");
                        setApplyStockImpact(false);
                        setManageDialog("rejected");
                      }}
                    >
                      ປະຕິເສດ
                    </Button>
                  </div>

                  <Modal
                    open={manageDialog !== null}
                    onOpenChange={(open) => {
                      if (!open) setManageDialog(null);
                    }}
                    title={
                      manageDialog === "resolved"
                        ? "ປິດເຄສ"
                        : manageDialog === "rejected"
                          ? "ປະຕິເສດເຄສ"
                          : ""
                    }
                    description="ໃສ່ຄຳອະທິບາຍ ແລ້ວກົດຢືນຢັນ"
                    size="md"
                  >
                    {manageDialog ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="res-note-detail-modal">
                            ຄຳອະທິບາຍ
                          </Label>
                          <textarea
                            id="res-note-detail-modal"
                            rows={4}
                            value={resolutionNote}
                            onChange={(e) => setResolutionNote(e.target.value)}
                            className="flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          />
                        </div>
                        {manageDialog === "resolved" ? (
                          <label
                            htmlFor="claim-apply-stock-impact-detail"
                            className="flex cursor-pointer items-start gap-2 text-sm"
                          >
                            <Checkbox
                              id="claim-apply-stock-impact-detail"
                              checked={applyStockImpact}
                              onCheckedChange={(v) =>
                                setApplyStockImpact(v === true)
                              }
                              className="mt-0.5"
                            />
                            <span>
                              ຕັດສະຕ໋ອກຊຳຮຸດຕາມລາຍບັນທັດ (serial ຕ້ອງຢູ່ສະຕ໋ອກ/ຮັບຄືນແລ້ວ)
                            </span>
                          </label>
                        ) : null}
                        <div className="flex justify-end gap-2 pt-1">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={setStatus.isPending}
                            onClick={() => setManageDialog(null)}
                          >
                            ຍົກເລີກ
                          </Button>
                          <Button
                            type="button"
                            className="bg-orange-600 hover:bg-orange-600"
                            disabled={setStatus.isPending}
                            onClick={async () => {
                              const note = resolutionNote.trim();
                              if (!note) {
                                toast.error("ກະລຸນາໃສ່ຄຳອະທິບາຍກ່ອນຢືນຢັນ");
                                return;
                              }
                              await setStatus.mutateAsync({
                                claimId: detail.id,
                                body: {
                                  status: manageDialog,
                                  resolutionNote: note,
                                  applyStockImpact:
                                    manageDialog === "resolved"
                                      ? applyStockImpact
                                      : false,
                                },
                              });
                              setManageDialog(null);
                              setResolutionNote("");
                              setApplyStockImpact(false);
                            }}
                          >
                            {setStatus.isPending ? <Loader /> : "ຢືນຢັນ"}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </Modal>
                </div>
              ) : null}

              {detail.resolutionNote ? (
                <p className="border-t pt-2 text-muted-foreground text-sm">
                  <span className="font-medium text-foreground">
                    ຄຳອະທິບາຍປິດເຄສ:{" "}
                  </span>
                  {detail.resolutionNote}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
